import React, { useState, useEffect } from 'react';
import { 
  Search, 
  ArrowUp, 
  ArrowDown, 
  Calendar,
  User,
  Package,
  FileText,
  Filter,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, InventoryTransaction } from '../../services/dataService';

const InventoryTransactions: React.FC = () => {
  const { tenantData } = useAuth();
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const typeOptions = ['all', 'in', 'out'];
  const sourceOptions = [
    { value: 'all', label: 'All Sources' },
    { value: 'manual', label: 'Manual' },
    { value: 'sale', label: 'Sales' },
    { value: 'adjustment', label: 'Adjustments' }
  ];
  const dateOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' }
  ];

  useEffect(() => {
    if (tenantData?.id) {
      loadTransactions();
    }
  }, [tenantData]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const data = await dataService.getInventoryTransactions(tenantData!.id);
      setTransactions(data);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactionsByDate = (transaction: InventoryTransaction) => {
    if (dateFilter === 'all') return true;
    
    const transactionDate = new Date(transaction.createdAt!);
    const now = new Date();
    
    switch (dateFilter) {
      case 'today':
        return transactionDate.toDateString() === now.toDateString();
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return transactionDate >= weekAgo;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return transactionDate >= monthAgo;
      default:
        return true;
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.performedBy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || transaction.type === typeFilter;
    const matchesSource = sourceFilter === 'all' || transaction.transactionSource === sourceFilter;
    const matchesDate = filterTransactionsByDate(transaction);
    
    return matchesSearch && matchesType && matchesSource && matchesDate;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl tablet:text-2xl font-bold text-gray-900 dark:text-white">
            Transaction History
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-base tablet:text-lg mt-1">
            Track all inventory movements and changes
          </p>
        </div>
        
        {/* Mobile Filter Toggle */}
        <div className="md:hidden">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className={`p-2 rounded-lg transition-colors ${
              showMobileFilters 
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {showMobileFilters ? (
              <X className="w-5 h-5" />
            ) : (
              <Filter className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={`card ${showMobileFilters ? 'block' : 'hidden'} md:block`}>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Type Filter */}
          <div className="md:w-40">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="input-field"
            >
              {typeOptions.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Types' : type === 'in' ? 'Stock In' : 'Stock Out'}
                </option>
              ))}
            </select>
          </div>

          {/* Source Filter */}
          <div className="md:w-40">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="input-field"
            >
              {sourceOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Filter */}
          <div className="md:w-48">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="input-field"
            >
              {dateOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile Filter Actions */}
        <div className="md:hidden mt-4 flex gap-3">
          <button
            onClick={() => {
              setSearchTerm('');
              setTypeFilter('all');
              setSourceFilter('all');
              setDateFilter('all');
            }}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
          <button
            onClick={() => setShowMobileFilters(false)}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Transactions List */}
      <div className="card w-full min-w-0">
        {filteredTransactions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No transactions found</h3>
            <p className="text-gray-600 dark:text-gray-300">
              {searchTerm || typeFilter !== 'all' || sourceFilter !== 'all' || dateFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Inventory transactions will appear here as you manage stock.'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="lg:hidden space-y-4">
              {filteredTransactions.map((transaction) => (
                <div key={transaction.id} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-3">
                  {/* Header Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`p-1 rounded-full ${
                        transaction.type === 'in' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                      }`}>
                        {transaction.type === 'in' ? (
                          <ArrowUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <ArrowDown className="w-4 h-4 text-red-600 dark:text-red-400" />
                        )}
                      </div>
                      <span className={`text-sm font-medium ${
                        transaction.type === 'in' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                      }`}>
                        {transaction.type === 'in' ? 'Stock In' : 'Stock Out'}
                      </span>
                    </div>
                    <span className={`font-medium text-lg ${
                      transaction.type === 'in' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {transaction.type === 'in' ? '+' : '-'}{transaction.quantity} {transaction.unit}
                    </span>
                  </div>

                  {/* Item Row */}
                  <div className="flex items-center space-x-2">
                    <Package className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <span className="font-medium text-gray-900 dark:text-white text-lg">
                      {transaction.itemName}
                    </span>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 mb-1">Reason</p>
                      <p className="text-gray-900 dark:text-white font-medium">{transaction.reason}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 mb-1">Source</p>
                      <div className="flex items-center space-x-1">
                        <div className={`p-1 rounded-full ${
                          transaction.transactionSource === 'sale' ? 'bg-blue-100 dark:bg-blue-900/30' :
                          transaction.transactionSource === 'manual' ? 'bg-gray-100 dark:bg-gray-700' : 'bg-yellow-100 dark:bg-yellow-900/30'
                        }`}>
                          {transaction.transactionSource === 'sale' ? (
                            <Package className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                          ) : transaction.transactionSource === 'manual' ? (
                            <User className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                          ) : (
                            <FileText className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
                          )}
                        </div>
                        <span className={`text-sm font-medium capitalize ${
                          transaction.transactionSource === 'sale' ? 'text-blue-700 dark:text-blue-300' :
                          transaction.transactionSource === 'manual' ? 'text-gray-700 dark:text-gray-300' : 'text-yellow-700 dark:text-yellow-300'
                        }`}>
                          {transaction.transactionSource || 'manual'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 mb-1">Performed By</p>
                      <div className="flex items-center space-x-1">
                        <User className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                        <span className="text-gray-900 dark:text-white">{transaction.performedBy}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 mb-1">Date</p>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                        <span className="text-gray-900 dark:text-white">
                          {new Date(transaction.createdAt!).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Cost Row */}
                  {transaction.totalCost && (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">Total Cost</span>
                      <span className="font-medium text-gray-900 dark:text-white text-lg">
                        ₹{transaction.totalCost.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden lg:block w-full min-w-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-sm xl:text-base whitespace-nowrap">
                        Type
                      </th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-sm xl:text-base whitespace-nowrap">
                        Item
                      </th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-sm xl:text-base whitespace-nowrap">
                        Quantity
                      </th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-sm xl:text-base whitespace-nowrap">
                        Reason
                      </th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-sm xl:text-base whitespace-nowrap">
                        Source
                      </th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-sm xl:text-base whitespace-nowrap">
                        Performed By
                      </th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-sm xl:text-base whitespace-nowrap">
                        Date
                      </th>
                      <th className="text-left py-3 px-3 font-medium text-gray-700 dark:text-gray-300 text-sm xl:text-base whitespace-nowrap">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="py-3 px-3 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <div className={`p-1 rounded-full ${
                              transaction.type === 'in' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                            }`}>
                              {transaction.type === 'in' ? (
                                <ArrowUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                              ) : (
                                <ArrowDown className="w-3 h-3 text-red-600 dark:text-red-400" />
                              )}
                            </div>
                            <span className={`text-xs font-medium ${
                              transaction.type === 'in' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                            }`}>
                              {transaction.type === 'in' ? 'In' : 'Out'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3 max-w-[120px]">
                          <div className="flex items-center space-x-1">
                            <Package className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            <span className="font-medium text-gray-900 dark:text-white truncate text-sm">
                              {transaction.itemName}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <span className={`font-medium text-sm ${
                            transaction.type === 'in' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {transaction.type === 'in' ? '+' : '-'}{transaction.quantity} {transaction.unit}
                          </span>
                        </td>
                        <td className="py-3 px-3 max-w-[100px]">
                          <span className="text-gray-700 dark:text-gray-300 truncate block text-sm">{transaction.reason}</span>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            <div className={`p-1 rounded-full ${
                              transaction.transactionSource === 'sale' ? 'bg-blue-100 dark:bg-blue-900/30' :
                              transaction.transactionSource === 'manual' ? 'bg-gray-100 dark:bg-gray-700' : 'bg-yellow-100 dark:bg-yellow-900/30'
                            }`}>
                              {transaction.transactionSource === 'sale' ? (
                                <Package className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                              ) : transaction.transactionSource === 'manual' ? (
                                <User className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                              ) : (
                                <FileText className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
                              )}
                            </div>
                            <span className={`text-xs font-medium capitalize ${
                              transaction.transactionSource === 'sale' ? 'text-blue-700 dark:text-blue-300' :
                              transaction.transactionSource === 'manual' ? 'text-gray-700 dark:text-gray-300' : 'text-yellow-700 dark:text-yellow-300'
                            }`}>
                              {transaction.transactionSource || 'manual'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3 max-w-[100px]">
                          <div className="flex items-center space-x-1">
                            <User className="w-3 h-3 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-300 truncate text-sm">{transaction.performedBy}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                            <span className="text-gray-700 dark:text-gray-300 text-sm">
                              {new Date(transaction.createdAt!).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          {transaction.totalCost ? (
                            <span className="font-medium text-gray-900 dark:text-white text-sm">
                              ₹{transaction.totalCost.toFixed(0)}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-sm">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Summary Stats */}
      {filteredTransactions.length > 0 && (
        <div className="grid grid-cols-1 tablet:grid-cols-3 gap-4">
          <div className="card">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
                <ArrowUp className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Stock In Transactions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {filteredTransactions.filter(t => t.type === 'in').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg">
                <ArrowDown className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Stock Out Transactions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {filteredTransactions.filter(t => t.type === 'out').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ₹{filteredTransactions
                    .filter(t => t.totalCost)
                    .reduce((sum, t) => sum + (t.totalCost || 0), 0)
                    .toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTransactions; 